import React, { useState, useEffect, useCallback } from 'react';
import { approvalsApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Check, X, Eye, House, Briefcase, Phone, CalendarX, Warning, Users, Trash } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const TYPES = { office: 'Ufficio', smart_working: 'Smart Working', leave: 'Ferie/Assenza', on_call: 'Reperibilità', rol: 'ROL', unpaid_leave: 'Permesso Non Retribuito' };
const TYPE_COLORS = { office: '#0A0A0A', smart_working: '#002FA7', leave: '#F59E0B', on_call: '#FF2A00', rol: '#8B5CF6', unpaid_leave: '#52525B' };

const ScheduleBadge = ({ type }) => {
  const color = TYPE_COLORS[type] || '#0A0A0A';
  return <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border" style={{ borderColor: color, color, backgroundColor: `${color}10` }}>{TYPES[type] || type}</span>;
};

const StatusBadge = ({ status }) => {
  const map = { pending: { c: 'border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/5', l: 'In Attesa' }, waiting: { c: 'border-[#52525B] text-[#52525B] bg-[#52525B]/5', l: 'In Coda' }, approved: { c: 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5', l: 'Approvata' }, rejected: { c: 'border-[#FF2A00] text-[#FF2A00] bg-[#FF2A00]/5', l: 'Rifiutata' } };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${s.c}`}>{s.l}</span>;
};

const ApprovalsPage = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allApprovals, setAllApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([approvalsApi.getPending(), approvalsApi.getAll()]);
      setPendingApprovals(pendingRes.data);
      setAllApprovals(allRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const handleAction = async () => {
    if (!selectedApproval || !actionType) return;
    setSubmitting(true);
    try {
      await approvalsApi.action(selectedApproval.id, actionType, comments);
      toast.success(actionType === 'approved' ? 'Richiesta approvata' : 'Richiesta rifiutata');
      setIsActionDialogOpen(false);
      fetchApprovals();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const getLevelStatus = (a, level) => a.approval_levels?.find(l => l.level === level)?.status || 'waiting';
  const getLevelInfo = (a, level) => a.approval_levels?.find(l => l.level === level) || {};

  const TeamAnalyticsWidget = ({ analytics, warning }) => {
    if (!analytics || analytics.total_members === 0) return null;
    return (
      <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Users size={18} weight="bold" className="text-[#002FA7]" />
          <span className="font-bold text-sm">Stato del Team nel Periodo</span>
        </div>
        <div className="grid grid-cols-4 gap-3 text-center">
          <div><p className="text-2xl font-black">{analytics.in_office}</p><p className="text-[10px] uppercase tracking-wider text-[#52525B]">In Ufficio</p></div>
          <div><p className="text-2xl font-black text-[#002FA7]">{analytics.smart_working}</p><p className="text-[10px] uppercase tracking-wider text-[#52525B]">Smart W.</p></div>
          <div><p className="text-2xl font-black text-[#F59E0B]">{analytics.leave}</p><p className="text-[10px] uppercase tracking-wider text-[#52525B]">Assenti</p></div>
          <div><p className="text-2xl font-black text-[#FF2A00]">{analytics.on_call}</p><p className="text-[10px] uppercase tracking-wider text-[#52525B]">Reperibili</p></div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-[#E4E4E7]">
          <span className="text-xs text-[#52525B]">Presenza in ufficio:</span>
          <span className={`font-bold ${analytics.office_percentage < 50 ? 'text-[#FF2A00]' : 'text-[#22C55E]'}`}>{analytics.office_percentage}%</span>
        </div>
        {/* Show individual members */}
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {analytics.members?.map((m, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs py-1">
              <span>{m.name}</span>
              <ScheduleBadge type={m.status} />
            </div>
          ))}
        </div>
        {warning && (
          <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
            <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
            <p className="text-sm text-[#FF2A00]">{warning}</p>
          </div>
        )}
      </div>
    );
  };

  const toggleSelect = (id) => { const n = new Set(selectedIds); n.has(id) ? n.delete(id) : n.add(id); setSelectedIds(n); };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} richieste?`)) return;
    try { await approvalsApi.bulkDelete([...selectedIds]); toast.success(`${selectedIds.size} richieste eliminate`); setSelectedIds(new Set()); fetchApprovals(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const renderTable = (approvals, showActions = false) => (
    <div className="border border-[#E4E4E7] overflow-x-auto">
      <table className="w-full data-table min-w-[700px]">
        <thead>
          <tr className="border-b-2 border-[#0A0A0A] bg-white">
            <th className="py-4 px-3 text-left w-10">
              <Checkbox checked={selectedIds.size > 0 && approvals.every(a => selectedIds.has(a.id))}
                onCheckedChange={() => { const all = approvals.every(a => selectedIds.has(a.id)); const n = new Set(selectedIds); approvals.forEach(a => all ? n.delete(a.id) : n.add(a.id)); setSelectedIds(n); }} />
            </th>
            <th className="py-4 px-4 text-left">Richiedente</th>
            <th className="py-4 px-4 text-left">Tipo</th>
            <th className="py-4 px-4 text-left">Periodo</th>
            <th className="py-4 px-4 text-left">Ore</th>
            <th className="py-4 px-4 text-left">Team Leader</th>
            <th className="py-4 px-4 text-left">Manager</th>
            <th className="py-4 px-4 text-left">Stato</th>
            <th className="py-4 px-4 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {approvals.length === 0 ? (
            <tr><td colSpan={9} className="py-12 text-center text-[#52525B]">Nessuna richiesta di approvazione.</td></tr>
          ) : approvals.map(a => {
            const details = a.schedule_details || {};
            return (
              <tr key={a.id} className={`border-b border-[#E4E4E7] hover:bg-[#F4F4F5] ${selectedIds.has(a.id) ? 'bg-[#002FA7]/5' : ''}`} data-testid={`approval-row-${a.id}`}>
                <td className="py-3 px-3"><Checkbox checked={selectedIds.has(a.id)} onCheckedChange={() => toggleSelect(a.id)} /></td>
                <td className="py-3 px-4"><p className="font-medium">{a.requester_name}</p><p className="text-xs text-[#52525B]">{a.requester_email}</p></td>
                <td className="py-3 px-4"><ScheduleBadge type={a.request_type} /></td>
                <td className="py-3 px-4 font-mono text-xs">{details.start_date || '-'}<br/>{details.end_date || '-'}</td>
                <td className="py-3 px-4 font-mono text-sm font-bold">{details.hours || 0}h</td>
                <td className="py-3 px-4"><StatusBadge status={getLevelStatus(a, 'team_leader')} /></td>
                <td className="py-3 px-4"><StatusBadge status={getLevelStatus(a, 'manager')} /></td>
                <td className="py-3 px-4"><StatusBadge status={a.status} /></td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setSelectedApproval(a); setIsViewDialogOpen(true); }} className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors relative z-10" data-testid={`view-approval-${a.id}`} title="Dettagli"><Eye size={16} weight="bold" /></button>
                    {showActions && a.status === 'pending' && (
                      <>
                        <button onClick={() => { setSelectedApproval(a); setActionType('approved'); setComments(''); setIsActionDialogOpen(true); }} className="w-8 h-8 flex items-center justify-center border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white transition-colors relative z-10" data-testid={`approve-${a.id}`} title="Approva"><Check size={16} weight="bold" /></button>
                        <button onClick={() => { setSelectedApproval(a); setActionType('rejected'); setComments(''); setIsActionDialogOpen(true); }} className="w-8 h-8 flex items-center justify-center border border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white transition-colors relative z-10" data-testid={`reject-${a.id}`} title="Rifiuta"><X size={16} weight="bold" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="approvals-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Approvazioni</h1>
          <p className="text-[#52525B] mt-1 text-sm">Gestisci e processa le richieste con dettagli del team</p>
        </div>
        {selectedIds.size > 0 && (
          <Button onClick={handleBulkDelete} className="bg-[#FF2A00] text-white px-5 py-2.5 font-medium hover:bg-[#D92400] rounded-none">
            <Trash size={18} weight="bold" className="mr-2" /> Elimina ({selectedIds.size})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="border border-[#E4E4E7] bg-white p-1 rounded-none">
            <TabsTrigger value="pending" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6">In Attesa ({pendingApprovals.length})</TabsTrigger>
            <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6">Tutte le Richieste</TabsTrigger>
          </TabsList>
          <TabsContent value="pending">{renderTable(pendingApprovals, true)}</TabsContent>
          <TabsContent value="all">{renderTable(allApprovals, false)}</TabsContent>
        </Tabs>
      )}

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#0A0A0A] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Dettagli Richiesta</DialogTitle></DialogHeader>
          {selectedApproval && (
            <div className="space-y-4 mt-4">
              {/* Request Details */}
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Richiedente</p><p className="font-medium">{selectedApproval.requester_name}</p></div>
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Tipo</p><ScheduleBadge type={selectedApproval.request_type} /></div>
              </div>
              
              {/* Schedule Details */}
              {selectedApproval.schedule_details && (
                <div className="border border-[#E4E4E7] p-4 bg-white">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3">Dettagli Richiesta</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div><p className="text-xs text-[#52525B]">Data Inizio</p><p className="font-mono font-bold">{selectedApproval.schedule_details.start_date}</p></div>
                    <div><p className="text-xs text-[#52525B]">Data Fine</p><p className="font-mono font-bold">{selectedApproval.schedule_details.end_date}</p></div>
                    <div><p className="text-xs text-[#52525B]">Ore Totali</p><p className="font-mono font-bold text-[#002FA7]">{selectedApproval.schedule_details.hours}h</p></div>
                  </div>
                  {selectedApproval.schedule_details.notes && (
                    <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
                      <p className="text-xs text-[#52525B]">Note</p>
                      <p className="text-sm">{selectedApproval.schedule_details.notes}</p>
                    </div>
                  )}
                  {selectedApproval.schedule_details.deduction_type && (
                    <div className="mt-2"><span className="text-xs bg-[#F59E0B]/10 border border-[#F59E0B] text-[#F59E0B] px-2 py-1 uppercase font-bold">
                      Deduzione: {selectedApproval.schedule_details.deduction_type === 'leave' ? 'Ferie' : selectedApproval.schedule_details.deduction_type === 'rol' ? 'ROL' : selectedApproval.schedule_details.deduction_type}
                    </span></div>
                  )}
                </div>
              )}

              {/* Team Analytics */}
              <TeamAnalyticsWidget analytics={selectedApproval.team_analytics} warning={selectedApproval.constraint_warning} />

              {/* Approval Workflow Status */}
              <div className="border-t border-[#E4E4E7] pt-4">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3">Flusso di Approvazione (Parallelo)</p>
                <div className="space-y-2">
                  {selectedApproval.approval_levels?.map((level, idx) => {
                    const info = level;
                    return (
                      <div key={idx} className="flex items-center justify-between py-3 px-4 bg-white border border-[#E4E4E7]">
                        <div>
                          <span className="font-medium">{level.level === 'team_leader' ? 'Team Leader' : 'Manager'}</span>
                          {info.approver_name && <span className="text-xs text-[#52525B] ml-2">({info.approver_name})</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={level.status} />
                          {info.approved_at && <span className="text-xs text-[#52525B]">{new Date(info.approved_at).toLocaleDateString('it-IT')}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedApproval.approval_levels?.some(l => l.comments) && (
                  <div className="mt-3">
                    <p className="text-xs text-[#52525B] mb-1">Commenti:</p>
                    {selectedApproval.approval_levels.filter(l => l.comments).map((l, i) => (
                      <p key={i} className="text-sm bg-[#FAFAFA] p-2 border border-[#E4E4E7] mb-1">
                        <b>{l.level === 'team_leader' ? 'TL' : 'Manager'}:</b> {l.comments}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-none border border-[#0A0A0A] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{actionType === 'approved' ? 'Approva Richiesta' : 'Rifiuta Richiesta'}</DialogTitle></DialogHeader>
          {selectedApproval && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4">
                <p className="text-sm mb-2"><b>{selectedApproval.requester_name}</b> - <ScheduleBadge type={selectedApproval.request_type} /></p>
                {selectedApproval.schedule_details && (
                  <p className="text-sm text-[#52525B]">
                    Periodo: {selectedApproval.schedule_details.start_date} → {selectedApproval.schedule_details.end_date} | {selectedApproval.schedule_details.hours}h
                  </p>
                )}
              </div>
              
              {/* Show team analytics before approving */}
              <TeamAnalyticsWidget analytics={selectedApproval.team_analytics} warning={selectedApproval.constraint_warning} />
              
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Commenti (Facoltativo)</label>
                <Textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Aggiungi commenti..." className="rounded-none border-[#E4E4E7] min-h-20" data-testid="approval-comments" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsActionDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button type="button" disabled={submitting} onClick={handleAction}
                  className={`flex-1 rounded-none ${actionType === 'approved' ? 'bg-[#22C55E] hover:bg-[#16A34A]' : 'bg-[#FF2A00] hover:bg-[#D92400]'} text-white`}
                  data-testid="confirm-action-btn">
                  {submitting ? 'Elaborazione...' : actionType === 'approved' ? 'Approva' : 'Rifiuta'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
